/**
 * Month calendar for choosing a distribution date.
 *
 * The previous control was a horizontally scrolling strip of days, which meant
 * picking a date three weeks out was a long swipe with no sense of where you
 * were. A calendar shows the month, so "the 30th" is one tap.
 *
 * Dates outside the server's booking window and blackout dates are rendered
 * disabled rather than hidden — a donor should see that the 12th exists and is
 * unavailable, not wonder why the month skips it.
 *
 * Everything here is local-date arithmetic on year/month/day integers. Using
 * UTC would put a donor east of Greenwich on the wrong day, which is the same
 * class of bug as `new Date('2026-08-30')` rendering as the 29th.
 */

import { memo, useCallback, useMemo, useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';

import { formatDate } from '@mfa/domain';
import { colors, radius, sizing, space } from '../../theme';
import { Button } from '../common/Button';
import { BackIcon, ChevronIcon } from '../common/Icon';
import { Text } from '../common/Text';
import { useLanguage, useT } from '../../core/i18n/LanguageProvider';
import { MONTHS, WEEKDAYS } from '../../core/i18n/language';

export interface CalendarModalProps {
  visible: boolean;
  /** Currently selected date, if any. */
  value: string | null;
  onCancel: () => void;
  onSelect: (isoDate: string) => void;
  minDate: Date;
  maxDate: Date;
  blackoutDates: string[];
  testID?: string;
}


/** Local-time ISO date. `toISOString` would shift the day east of Greenwich. */
function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

interface Cell {
  day: number | null;
  iso: string;
  disabled: boolean;
}

export const CalendarModal = memo(function CalendarModal({
  visible,
  value,
  onCancel,
  onSelect,
  minDate,
  maxDate,
  blackoutDates,
  testID = 'calendar',
}: CalendarModalProps) {
  const t = useT();
  const { language, isRTL } = useLanguage();
  const months = MONTHS[language];
  const weekdays = WEEKDAYS[language];
  const initial = value ? new Date(`${value}T00:00:00`) : minDate;
  const [cursor, setCursor] = useState({
    year: initial.getFullYear(),
    month: initial.getMonth(),
  });
  const [pending, setPending] = useState<string | null>(value);

  const min = startOfDay(minDate);
  const max = startOfDay(maxDate);

  const cells = useMemo<Cell[]>(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    // Monday-first: JS gives Sunday as 0, so shift it to the end of the week.
    const leading = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();

    const blanks: Cell[] = Array.from({ length: leading }, (_, i) => ({
      day: null,
      iso: `blank-${i}`,
      disabled: true,
    }));
    const days: Cell[] = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const date = new Date(cursor.year, cursor.month, day);
      const key = iso(cursor.year, cursor.month, day);
      return {
        day,
        iso: key,
        disabled: date < min || date > max || blackoutDates.includes(key),
      };
    });
    return [...blanks, ...days];
  }, [cursor, min, max, blackoutDates]);

  // A month with nothing selectable is a dead end; the arrow is disabled there.
  const canGoBack =
    new Date(cursor.year, cursor.month, 1) > new Date(min.getFullYear(), min.getMonth(), 1);
  const canGoForward =
    new Date(cursor.year, cursor.month, 1) < new Date(max.getFullYear(), max.getMonth(), 1);

  const step = useCallback((delta: number) => {
    setCursor((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  }, []);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
      testID={testID}
    >
      {/* Tapping the scrim cancels, which is what a dimmed background implies. */}
      <Pressable style={[styles.scrim, { direction: isRTL ? 'rtl' : 'ltr' }]} onPress={onCancel} accessibilityLabel={t('Close calendar')}>
        <Pressable style={styles.sheet} onPress={() => {}} accessibilityViewIsModal>
          <View style={styles.monthRow}>
            <Pressable
              onPress={() => step(-1)}
              disabled={!canGoBack}
              accessibilityRole="button"
              accessibilityLabel={t('Previous month')}
              accessibilityState={{ disabled: !canGoBack }}
              testID={`${testID}-prev`}
              style={[styles.arrow, !canGoBack && styles.arrowDisabled]}
            >
              <BackIcon />
            </Pressable>

            <Text variant="cardTitle" align="center" testID={`${testID}-month`}>
              {months[cursor.month]} {cursor.year}
            </Text>

            <Pressable
              onPress={() => step(1)}
              disabled={!canGoForward}
              accessibilityRole="button"
              accessibilityLabel={t('Next month')}
              accessibilityState={{ disabled: !canGoForward }}
              testID={`${testID}-next`}
              style={[styles.arrow, !canGoForward && styles.arrowDisabled]}
            >
              <ChevronIcon />
            </Pressable>
          </View>

          <View style={styles.weekdays}>
            {weekdays.map((label) => (
              <Text key={label} variant="caption" tone="tertiary" align="center" style={styles.cell}>
                {label}
              </Text>
            ))}
          </View>

          <View style={styles.grid}>
            {cells.map((cell) =>
              cell.day === null ? (
                <View key={cell.iso} style={styles.cell} />
              ) : (
                <Pressable
                  key={cell.iso}
                  disabled={cell.disabled}
                  onPress={() => setPending(cell.iso)}
                  accessibilityRole="button"
                  accessibilityLabel={formatDate(cell.iso)}
                  accessibilityState={{
                    disabled: cell.disabled,
                    selected: pending === cell.iso,
                  }}
                  testID={`${testID}-day-${cell.iso}`}
                  style={[styles.cell, styles.day]}
                >
                  <View
                    style={[
                      styles.dayInner,
                      pending === cell.iso && styles.daySelected,
                    ]}
                  >
                    <Text
                      variant="body"
                      align="center"
                      tone={
                        cell.disabled ? 'disabled' : pending === cell.iso ? 'onBrand' : 'primary'
                      }
                    >
                      {cell.day}
                    </Text>
                  </View>
                </Pressable>
              ),
            )}
          </View>

          <View style={styles.actions}>
            <Button
              label={t('Cancel')}
              variant="secondary"
              onPress={onCancel}
              style={styles.action}
              testID={`${testID}-cancel`}
            />
            <Button
              label={t('Select')}
              onPress={() => pending && onSelect(pending)}
              disabled={!pending}
              style={styles.action}
              testID={`${testID}-confirm`}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
});

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.lg,
    backgroundColor: colors.bg.scrim,
  },
  sheet: {
    width: '100%',
    maxWidth: sizing.formColumnMaxWidth,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
    gap: space.md,
  },
  monthRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  arrow: {
    minWidth: sizing.touchTarget,
    minHeight: sizing.touchTarget,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.sm,
  },
  arrowDisabled: {
    opacity: 0.3,
  },
  weekdays: {
    flexDirection: 'row',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cell: {
    // Seven columns. A fraction rather than a fixed width so the grid fits any
    // phone without a horizontal scrollbar.
    width: `${100 / 7}%`,
  },
  day: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: space.xxs,
  },
  dayInner: {
    width: sizing.touchTarget - 4,
    height: sizing.touchTarget - 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.full,
  },
  daySelected: {
    backgroundColor: colors.bg.brand,
  },
  actions: {
    flexDirection: 'row',
    gap: space.md,
    marginTop: space.xs,
  },
  action: {
    flex: 1,
  },
});
